<template>
  <!-- eslint-disable prefer-standard-components/no-vuetify-inputs -->
  <v-col
    v-if="enabled"
    :cols="cols"
    :lg="lg"
    :md="md"
  >
    <v-row dense>
      <v-col class="labeled-input">
        {{ $bct(message) }}
        <HelpIcon
          :document="helpDocument"
          :on-help-click="openHelpFlyout"
        />
      </v-col>
    </v-row>
    <v-row dense>
      <v-col cols="4">
        <v-text-field
          v-model.number="months"
          @update:model-value="syncModelFromFields"
          :append-inner-icon="locked ? 'lock' : ''"
          :aria-label="$bct('age')"
          :data-testid="dataTestId"
          :density="dense ? 'compact' : undefined"
          :disabled="locked"
          :error="errorMessages.length > 0"
          :rules="validationRules"
          maxlength="2"
          min="0"
          type="number"
          variant="filled"
          hide-details
          round
          tile
        />
      </v-col>
      <v-col cols="8">
        <v-select
          v-model="period"
          @update:model-value="syncModelFromFields"
          :append-inner-icon="locked ? 'lock' : ''"
          :aria-label="$bct('months or years')"
          :density="dense ? 'compact' : undefined"
          :disabled="locked"
          :item-title="(item) => $bct(item.text)"
          :items="[
            { text: 'months', value: 'months' },
            { text: 'years', value: 'years' },
          ]"
          variant="filled"
          hide-details
          tile
        />
      </v-col>
    </v-row>
    <v-row
      v-if="errorMessages.length > 0"
      dense
    >
      <v-col>
        <div class="text-error text-caption">
          {{ errorMessages[0] }}
        </div>
      </v-col>
    </v-row>
  </v-col>
</template>

<script setup>
import HelpIcon from '@/shared/components/form/HelpIcon.vue';
import propsToRefs from '@/shared/utils/propsToRefs';
import useLabeledField from '@/shared/composables/useLabeledField';
import useSchematizedField from '@/shared/composables/useSchematizedField';

const $bct = inject('translator');

defineEmits(useLabeledField.emits);

const props = defineProps({
  ...useLabeledField.props,
  ...useSchematizedField.props,
  dataTestId: {
    type: String,
    default: null,
  },
  minBoundMonths: {
    type: Number,
    default: undefined,
  },
  maxBoundMonths: {
    type: Number,
    default: undefined,
  },
  rules: {
    type: Array,
    default: () => [],
  },
});

const localValue = defineModel({ type: [String, Number] });

const months = ref(initialNumericDisplay(localValue.value));
const period = ref('months');

const { handleBlur, handleUpdateModelValue } = useLabeledField(
  ...propsToRefs(props, useLabeledField.paramKeys),
);

const { enabled, helpDocument, locked, openHelpFlyout } = useSchematizedField(
  ...propsToRefs(props, useSchematizedField.paramKeys),
);

const validationRules = computed(() => {
  return [...props.rules, ageWithinBoundsRule];
});

const errorMessages = computed(() => {
  return validationRules.value
    .map((rule) => rule(months.value))
    .filter((result) => result !== true);
});

watch(localValue, (newVal) => {
  if (newVal) {
    if (period.value === 'years' && months.value * 12 !== newVal) {
      period.value = 'months';
      months.value = newVal;
    } else if (period.value === 'months' && months.value !== newVal) {
      period.value = 'months';
      months.value = newVal;
    }
  } else {
    period.value = 'months';
    months.value = newVal;
  }
});

function ageWithinBoundsRule(value) {
  const monthValue = valueToMonths(value);
  if (monthValue == null) {
    return true;
  }
  if (props.minBoundMonths != null && monthValue < props.minBoundMonths) {
    return `${$bct(messageLabel.value)} ${$bct('must be at least')} ${props.minBoundMonths} ${$bct('months')}`;
  }
  if (props.maxBoundMonths != null && monthValue > props.maxBoundMonths) {
    return `${$bct(messageLabel.value)} ${$bct('must be at most')} ${props.maxBoundMonths} ${$bct('months')}`;
  }
  return true;
}

const messageLabel = computed(() => props.message || 'Age');

function valueToMonths(value) {
  if (value == null || value === '') {
    return null;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return null;
  }
  if (period.value === 'years') {
    return numeric * 12;
  }
  return numeric;
}

function syncModelFromFields() {
  const newVal = valueToMonths(months.value);
  handleUpdateModelValue(newVal);
  handleBlur(newVal);
}

function initialNumericDisplay(value) {
  if (value == null || value === '') {
    return value;
  }
  const numeric = Number(value);
  if (Number.isNaN(numeric)) {
    return value;
  }
  return numeric;
}
</script>
